import { useMemo, useRef } from "react";
import Table from "views/merchandise/tables/BulkActionsTable";
import useCustomTableFilters from "hooks/useCustomTableFilters";
import { useAppSelector } from "redux/hooks";
import useDSPInventoryData from "./useDSPInventoryData";
import { HiExternalLink } from "react-icons/hi";
import InventoryExpander from "./InventoryExpander";

const DSPInventoryTable = ({ adGroupId }) => {
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const tableRef = useRef();
  const TABLE_ID = "dspInventoryTable";
  const filterProps = useCustomTableFilters(TABLE_ID);

  const {
    data: dspInventoryStatsData,
    metadata: dspInventoryMetadata,
    isLoading,
  } = useDSPInventoryData("site", true, { line_item_id: adGroupId });

  const dspInventoryTableData = useMemo(() => {
    return dspInventoryStatsData.map((row) => {
      const metadata = dspInventoryMetadata?.find((meta) => meta.site === row.site);
      return { ...row, ...metadata };
    });
  }, [dspInventoryStatsData, dspInventoryMetadata]);

  const columnSpecs = useMemo(
    () => [
      { key: "dsp_sales", compareKey: "dsp_sales_percent" },
      { key: "dsp_total_sales", compareKey: "dsp_total_sales_percent" },
      { key: "dsp_ecpm", compareKey: "dsp_ecpm_percent" },
      { key: "dsp_impressions", compareKey: "dsp_impressions_percent" },
      { key: "dsp_clicks", compareKey: "dsp_clicks_percent" },
      { key: "dsp_cost", compareKey: "dsp_cost_percent" },
      { key: "dsp_total_purchases", compareKey: "dsp_total_purchases_percent" },
      { key: "dsp_units", compareKey: "dsp_units_percent" },
      { key: "dsp_total_units", compareKey: "dsp_total_units_percent", options: { checked: false } },
      { key: "dsp_ctr", compareKey: "dsp_ctr_delta" },
      {
        key: "dsp_total_acos",
        compareKey: "dsp_total_acos_delta",
        options: { checked: false },
      },
      {
        key: "dsp_total_roas",
        compareKey: "dsp_total_roas_delta",
        options: { checked: false },
      },
      {
        key: "dsp_total_detail_page_views",
        compareKey: "dsp_total_detail_page_views_percent",
        options: { checked: false },
      },
      {
        key: "dsp_total_detail_page_view_rate",
        compareKey: "dsp_total_detail_page_view_rate_delta",
        options: { checked: false },
      },
      {
        key: "dsp_total_add_to_cart",
        compareKey: "dsp_total_add_to_cart_percent",
        options: { checked: false },
      },
      {
        key: "dsp_new_to_brand_detail_page_views",
        compareKey: "dsp_new_to_brand_detail_page_views_percent",
        options: { checked: false },
      },
      {
        key: "dsp_new_to_brand_detail_page_views_rate",
        compareKey: "dsp_new_to_brand_detail_page_views_rate_delta",
        options: { checked: false },
      },
      {
        key: "dsp_total_new_to_brand_dpvs",
        compareKey: "dsp_total_new_to_brand_dpvs_percent",
        options: { checked: false },
      },
      {
        key: "dsp_total_new_to_brand_dpv_rate",
        compareKey: "dsp_total_new_to_brand_dpv_rate_delta",
        options: { checked: false },
      },
      {
        key: "dsp_brand_searches",
        compareKey: "dsp_brand_searches_percent",
        options: { checked: false },
      },
      {
        key: "dsp_brand_searches_rate",
        compareKey: "dsp_brand_searches_rate_delta",
        options: { checked: false },
      },
    ],
    [],
  );
  return (
    <Table
      marketPlace={marketPlace}
      tableRef={tableRef}
      tableId={TABLE_ID}
      isExpandable={true}
      isSelectable={false}
      titleCol={{
        id: "site",
        isStatic: true,
        Header: "Site",
        accessor: "text",
        format: "text",
        style: { width: "300px" },
        width: 300,
        Cell: ({ row }) => {
          const { site } = row?._original ?? {};

          return (
            <span
              style={{
                textDecoration: "none",
                fontWeight: "500",
                fontSize: "1.5rem",
                cursor: "pointer",
              }}
            >
              {/* favicon here */}
              <img
                src={`https://www.google.com/s2/favicons?domain=${site}`}
                alt={site}
                // If favicon not found matching site hide the broken alt name
                onError={(e) => {
                  e.target.style.display = "none";
                }}
                key={site}
              />{" "}
              {site}{" "}
              <a href={`https://${site}`} target="_blank" rel="noopener noreferrer" title="View Site in New Tab">
                <HiExternalLink />
              </a>
            </span>
          );
        },
        Filter: () => null,
      }}
      data={dspInventoryTableData}
      columnSpecs={columnSpecs}
      getSearchCriteria={(row) => {
        const { site } = row ?? {};
        return `${site}`;
      }}
      tableRowDescription={"Inventories"}
      defaultSorted={[{ id: "dsp_sales", desc: true }]}
      isLoading={isLoading}
      filterWidget={true}
      hideFilterToggleIcon={true}
      hideResetFiltersIcon={false}
      {...filterProps}
      handleBulkChanges={() => {}}
      minRows={1}
      selectionKey={"id"}
      ExpandedContent={({ row }) => <InventoryExpander row={row.row} expanderFilters={null} />}
    />
  );
};

export default DSPInventoryTable;
